<i18n locale="en">
  {
    "email": {
      "label": "Email",
      "placeholder": "Enter your email"
    },
    "password": {
      "label": "Password",
      "placeholder": "Enter your password",
      "link-text": "Forgot Password"
    },
    "actions": {
      "login-button": "Sign In"
    },
    "errors": {
      "auth-failed": "Incorrect email or password",
      "email-required": "Valid email address required",
      "password-required": "Password required"
    }
  }
</i18n>
<template>
  <OwnForm :processing="isProcessing" :submit-handler="onSubmit">
    <div class="flex-col gap-10">
      <div class="flex-col gap-8">
        <OwnInputContainer
          :label="t('email.label')"
          :errors="
            hasAuthError ? [''] : mapErrorMessage(formState.email.$errors)
          "
        >
          <OwnInput
            v-model.trim="emailAddressFromInput"
            autocomplete="email"
            autocapitalize="off"
            :placeholder="t('email.placeholder')"
            type="email"
            @update:model-value="clearGenericError"
          />
        </OwnInputContainer>
        <div
          :class="['flex-col', isRenderingPasswordError ? 'gap-4' : 'gap-2']"
        >
          <OwnInputContainer
            :label="t('password.label')"
            :errors="
              hasAuthError
                ? [t('errors.auth-failed')]
                : mapErrorMessage(formState.password.$errors)
            "
          >
            <OwnInput
              v-model="password"
              type="password"
              autocomplete="current-password"
              :placeholder="t('password.placeholder')"
              @update:model-value="clearGenericError"
            />
          </OwnInputContainer>

          <RouterLink :to="forgotPasswordRoute">
            <OwnType
              :text="t('password.link-text')"
              color="brand"
              el="span"
              variant="button-small"
            />
          </RouterLink>
        </div>
      </div>

      <div class="flex-col gap-3">
        <OwnButton
          type="submit"
          :processing="isProcessing"
          :text="t('actions.login-button')"
          variant="primary"
        />

        <slot name="legalInfo" />
      </div>

      <OwnRule type="dashed" />

      <slot name="postButtonActions" />
    </div>
  </OwnForm>
</template>
<script lang="ts" setup>
import { useVuelidate, ValidationArgs } from '@vuelidate/core'
import { email, required, helpers } from '@vuelidate/validators'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useAuthActions } from '@/auth/useAuthActions'
import { OwnButton } from '@/ui/OwnButton'
import { OwnForm } from '@/ui/OwnForm'
import { OwnInput } from '@/ui/OwnInput'
import { OwnInputContainer } from '@/ui/OwnInputContainer'
import { OwnRule } from '@/ui/OwnRule'
import { OwnType } from '@/ui/OwnType'
import { mapErrorMessage } from '@/utils/vuelidate'

const { login } = useAuthActions()
const { t } = useI18n()

const hasAuthError = ref<boolean>(false)
const isProcessing = ref<boolean>(false)
const emailAddressFromInput = ref<string>('')
const password = ref<string>('')

const rules: ValidationArgs<{ email: string; password: string }> = {
  email: {
    email,
    required: helpers.withMessage(t('errors.email-required'), required),
  },
  password: {
    required: helpers.withMessage(t('errors.password-required'), required),
  },
}

const formState = useVuelidate(
  rules,
  { email: emailAddressFromInput, password },
  {
    $autoDirty: true,
  }
)

const forgotPasswordRoute = computed(() => {
  const baseRoute = { name: 'auth.forgot-password' }
  if (!formState.value.email.$invalid) {
    return {
      ...baseRoute,
      query: { email: emailAddressFromInput.value },
    }
  }

  return baseRoute
})

const dispatchLogin = async () => {
  try {
    isProcessing.value = true

    await login({
      email: emailAddressFromInput.value,
      password: password.value,
    })
  } catch (error) {
    hasAuthError.value = true
  } finally {
    isProcessing.value = false
  }
}

const onSubmit = async () => {
  const isFormValid = await formState.value.$validate()
  if (isFormValid) {
    await dispatchLogin()
  }
}

const clearGenericError = () => {
  hasAuthError.value = false
}

const isRenderingPasswordError = computed(() => {
  return (
    (formState.value.password.$invalid && formState.value.password.$dirty) ||
    hasAuthError.value
  )
})
</script>
